import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  LIBELLES_LANGUE_CHARTE,
  declarationsCharte,
  lireDeclarations,
  lireLienCharte,
  type BaremeApplicable,
  type CharteSigneeResume,
  type GroupeBareme,
  type LienCharte,
  type ReponsesDeclarations,
} from '@rh/shared';
import { appel } from '../api';
import { Chargement, Erreur } from '../composants/Etat';
import { SectionPhotos } from '../composants/SectionPhotos';
import { dateHeure } from '../format';
import { aujourdhui, usePhotosScan } from '../photos-scan';

/**
 * Scan de la charte signée (docs/UI.md § Scan de documents) : photos à
 * l'appareil, contrôle de netteté, rattachement par QR code, puis report des
 * cases cochées, qui donnent les notes tabac et alcool de l'évaluation.
 */
export function ScanCharte() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const photos = usePhotosScan();
  const [chartes, setChartes] = useState<CharteSigneeResume[] | null>(null);
  const [groupe, setGroupe] = useState<GroupeBareme | null>(null);
  const [charteId, setCharteId] = useState('');
  const [reponses, setReponses] = useState<ReponsesDeclarations>({});
  const [signeeLe, setSigneeLe] = useState(aujourdhui());
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    appel<CharteSigneeResume[]>(`/candidatures/${id}/chartes`)
      .then((liste) => {
        setChartes(liste);
        const enAttente = liste.filter((c) => !c.documentScanId);
        if (enAttente.length === 1) setCharteId((actuelle) => actuelle || enAttente[0]!.id);
      })
      .catch((e: Error) => setErreur(e.message));
    appel<BaremeApplicable>(`/candidatures/${id}/bareme`)
      .then((bareme) => setGroupe(bareme.groupe))
      .catch(() => setGroupe(null));
  }, [id]);

  if (!chartes && !erreur) return <Chargement quoi="des chartes" />;

  const enAttente = chartes?.filter((c) => !c.documentScanId) ?? [];
  const declarations = groupe ? declarationsCharte(groupe) : [];
  const lecture = groupe ? lireDeclarations(groupe, reponses) : null;

  async function ajouter(fichiers: FileList | null): Promise<void> {
    setErreur(null);
    setInfo(null);
    const textes = await photos.ajouter(fichiers);
    const lien = textes.map(lireLienCharte).find((l): l is LienCharte => l !== null);
    if (!lien) return;
    if (lien.candidatureId !== id) {
      setErreur('Ce QR code appartient à la charte d’un autre candidat : vérifier le document photographié.');
    } else if (!enAttente.some((c) => c.id === lien.charteSigneeId)) {
      setErreur('Ce QR code désigne une charte déjà scannée ou inconnue pour ce candidat.');
    } else {
      setCharteId(lien.charteSigneeId);
      setInfo('Charte reconnue par son QR code.');
    }
  }

  async function enregistrer(): Promise<void> {
    if (photos.floues > 0 && !window.confirm(`${photos.floues} photo(s) semblent floues. Enregistrer quand même ?`)) {
      return;
    }
    setErreur(null);
    setEnvoi(true);
    try {
      await appel<CharteSigneeResume[]>(`/candidatures/${id}/charte/signee`, {
        method: 'POST',
        body: photos.formulaire({ charteSigneeId: charteId, declarations: JSON.stringify(reponses), signeeLe }),
      });
      navigate(`/candidatures/${id}`);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <article className="scan-charte">
      <div className="entete-annonce">
        <div>
          <h2>Charte signée</h2>
          <p className="meta">
            <Link to={`/candidatures/${id}`}>← Fiche</Link>
          </p>
        </div>
      </div>

      {chartes && enAttente.length === 0 && (
        <p className="signal">
          Aucune charte en attente de signature pour ce candidat : éditer d’abord la charte depuis sa fiche.
        </p>
      )}

      <SectionPhotos
        photos={photos}
        consigne="Une photo par page, à plat, bien éclairée. Le QR code en haut de la première page rattache le scan."
        onAjout={(fichiers) => void ajouter(fichiers)}
      />

      <section className="bloc">
        <h3>2. Charte</h3>
        {enAttente.length > 0 ? (
          <select value={charteId} aria-label="Charte signée" onChange={(e) => setCharteId(e.target.value)}>
            <option value="">Choisir la charte photographiée…</option>
            {enAttente.map((charte) => (
              <option key={charte.id} value={charte.id}>
                {charte.id.slice(0, 8).toUpperCase()} — éditée le {dateHeure(charte.editeeLe)} —{' '}
                {LIBELLES_LANGUE_CHARTE[charte.langue]}
              </option>
            ))}
          </select>
        ) : (
          <p className="mention">—</p>
        )}
        <label className="champ">
          Signée le
          <input type="date" value={signeeLe} max={aujourdhui()} onChange={(e) => setSigneeLe(e.target.value)} />
        </label>
      </section>

      <section className="bloc">
        <h3>3. Cases cochées</h3>
        <p className="mention">Reporter exactement ce que le candidat a coché ; laisser vide ce qui ne l’est pas.</p>
        {declarations.map((declaration) => (
          <fieldset key={declaration.cle} className="declaration-scan">
            <legend>{declaration.question.fr}</legend>
            <div className="choix-multiples">
              {declaration.choix.map((choix) => {
                const actif = reponses[declaration.cle] === choix.cle;
                return (
                  <button
                    key={choix.cle}
                    type="button"
                    aria-pressed={actif}
                    className={actif ? 'actif' : ''}
                    onClick={() =>
                      setReponses((precedentes) => {
                        const suivantes = { ...precedentes };
                        if (actif) delete suivantes[declaration.cle];
                        else suivantes[declaration.cle] = choix.cle;
                        return suivantes;
                      })
                    }
                  >
                    {choix.texte.fr}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}
        {lecture && (
          <div className="signal">
            <strong>Pour l’évaluation :</strong> tabac {lecture.scores.tabac ?? '—'}/5 · alcool{' '}
            {lecture.scores.alcool ?? '—'}/5
            {lecture.eliminatoire && <div className="alerte">Éliminatoire : {lecture.eliminatoire.raison}.</div>}
            {lecture.aTrancher.length > 0 && (
              <ul>
                {lecture.aTrancher.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {erreur && <Erreur message={erreur} />}
      {info && <p className="signal">{info}</p>}
      <div className="actions">
        <button
          type="button"
          className="bouton"
          disabled={envoi || photos.traitement || photos.pages.length === 0 || !charteId}
          onClick={() => void enregistrer()}
        >
          {envoi ? 'Enregistrement…' : 'Enregistrer la charte signée'}
        </button>
      </div>
    </article>
  );
}
